import { Organizer, OrganizerType } from "../../../shared/types/OrganizerTypes"
import { getOrderPerson } from "../../../shared/utils/getOrderPerson"
import { OutlookMeetingValue, OutlookSessionType, SessionValue } from "./outlookCreateOrderTypes"

export const isOutlookMeetingValue = (v: SessionValue): v is OutlookMeetingValue =>
    v.type === OutlookSessionType.OUTLOOK_MEETING

/**
 * The ORGANIZER entry that carries the Outlook calendar address.
 *
 * For orders created from Outlook, `ORGANIZER` is reserved for the *calendar* the meeting lives in
 * — its email is what the API authorizes workgroup members by — while the person travels as
 * `CREATOR`. Only the add-in can read that address, so it must be passed on unchanged.
 *
 * Recognised by "has an email, has no user id": the API returns `id: 0` (not a missing field) for
 * organizers that reference no user, and a free-text responsible is id-less too but never carries
 * an email — so requiring one keeps those recognised as people.
 */
export function isCalendarOrganizer(organizer: Organizer): boolean {
    return (organizer.type ?? organizer.organizerType) === OrganizerType.ORGANIZER && !organizer.id && !!organizer.email
}

export function findCalendarOrganizer(organizers?: Organizer[]): Organizer | undefined {
    return organizers?.find(isCalendarOrganizer)
}

/**
 * The calendar entry as a request payload. The API echoes it back with `id: 0` and both
 * discriminators; neither belongs in a write, and re-sending `id: 0` is the "unlinked label"
 * hazard. The caller adds the discriminator its endpoint expects.
 */
export function toCalendarOrganizerPayload(organizer: Organizer): Pick<Organizer, "name" | "email"> {
    return { name: organizer.name, email: organizer.email }
}

/**
 * Who to seed the "meeting responsible" field with.
 *
 * `getOrderPerson` ranks ORGANIZER above CREATOR — a contract shared with the Outlook add-in and
 * deliberately left alone. That ranking assumes ORGANIZER is a person, which stops being true once
 * it carries the calendar: `person` then resolves to a mailbox address. Reachable both before an
 * Outlook order is saved (its only organizers come from the session) and after, on an order that
 * ended up without a RESPONSIBLE.
 */
export function getResponsibleSeed(organizers?: Organizer[]): Organizer | undefined {
    const { person, creator } = getOrderPerson(organizers)

    return person && isCalendarOrganizer(person) ? creator : person
}
